import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { RequestError, Service } from 'webapp-types';
import { DesignerLayout } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DesignerTopbarNotificationsComponent from 'webapp/components/designer/topbar/notifications';
import DesignerTopbarPagerComponent from 'webapp/components/designer/topbar/pager';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import SaveIndicatorComponent from 'webapp/components/save-indicator';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';
import TopbarZoomComponent from 'webapp/components/topbar-zoom';

import type Board from 'webapp/models-custom/board';
import BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import type BoardLoop from 'webapp/models-custom/board-loop';

export interface Args {
	selectedBoard: Board | null;
	selectedBoardLoop: BoardLoop | null;
	disableBoardsDropdown: boolean;
	onBoardUpdate: (board: Board) => void;
	onBoardChange: (board: Board) => void;
	onBoardLoopChange: (boardLoop: BoardLoop) => void;
	onExitDesigner: (transitToHome: boolean) => void;
}

export default class DesignerTopbarContainerComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('tour') declare tourService: Service.Tour;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;

	videoUrl = 'https://player.vimeo.com/video/951418821?autoplay=1';

	get isWallView() {
		return this.args.selectedBoard?.isWallPrimary;
	}

	get isAccountTemplate() {
		return !!this.args.selectedBoard?.isAccountTemplate;
	}

	get showHowToUseDesigner() {
		const isNewAndHaveNotSeenDesigner = this.tourService.isEligibleForDesignerIntroTour;
		const hasLessThanThreeBoards = this.billingService.boardsCurrent < 3;
		const isWhiteLabelingEnabled = this.accountService.whiteLabelingEnabled;

		return (hasLessThanThreeBoards || isNewAndHaveNotSeenDesigner) && !isWhiteLabelingEnabled;
	}

	@action
	saveBoard(board: Board): void {
		this.args.onBoardUpdate(board);
	}

	@action
	openBoard(board: Board): void {
		this.args.onBoardChange(board);
	}

	@action
	openBoardLoop(loop: BoardLoop): void {
		this.args.onBoardLoopChange(loop);
	}

	@action
	changeBoardView(isWallView: boolean): void {
		const newModeId = isWallView ? DesignerLayout.DESKTOP : DesignerLayout.MOBILE,
			selectedBoard = this.args.selectedBoard;

		if (selectedBoard?.canCurrentUserEdit) {
			selectedBoard.primaryView = newModeId;
			this.saveBoard(selectedBoard);
			// selectedBoard.save().catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			this.analyticsService.event(TrackingEvent.DESIGNER_PRIMARY_VIEW_CHANGED, {
				view: isWallView ? 'desktop' : 'mobile',
			});
		}
	}

	@action
	exitDesigner(transitToHome = false): void {
		this.args.onExitDesigner(transitToHome);
	}

	@action
	updateFromTemplate() {
		// check if we are dealing with a template
		const templateId = this.args.selectedBoard?.templateId;
		if (!templateId) {
			return;
		}

		const templateBoardId = this.args.selectedBoard?.numericId;
		if (!templateBoardId) {
			return;
		}

		this.analyticsService.event(TrackingEvent.TEMPLATE_DATABOARDS_UPDATE_POPUP);

		const popup = this.mmBoardsService.createBoardsSelectListModal();

		popup.open({
			model: [],
			maxSelectable: 100,
			onConfirm: (selected: number[]) => {
				return new Promise((resolve) => {
					const onConfirm = () => {
						this.analyticsService.event(TrackingEvent.TEMPLATE_DATABOARDS_UPDATED, {
							selected_boards: selected.length,
						});
						void this.mmTemplatesService.updateBoardsFromTemplate(
							templateBoardId,
							selected,
						);
						resolve(true);
					};

					this.popupService.openConfirmPopup({
						onConfirm,
						onReject: () => {
							resolve(false);
						},
						title: `Are you sure you want to update selected dashboards (${selected.length})?`,
						text: `<strong>This action cannot be undone.</strong> All selected dashboards (${selected.length}) will be updated and overridden with the layout and settings from the template.`,
						confirmAction: 'Update',
						cancelAction: 'Cancel',
						isWarning: true,
					});
				});
			},
			fetchBoardsFromTemplate: () => {
				return this.mmTemplatesService
					.fetchBoardsFromTemplate(templateBoardId)
					.then((reply) => {
						return reply.map((boardData) => {
							return new BoardFromAnotherSpace(boardData);
						});
					})
					.catch((errorData: RequestError) => {
						this.requestService.showAllValidationErrors(errorData);
						throw errorData; // throw error forward to the popup so we can close it
					});
			},
		});
	}

	@action
	openDesignerVideoPopup() {
		this.navigationService.openVideoPopup(this.videoUrl);
	}
	<template>
		<div id="topbar">
			<DesignerTopbarPagerComponent
				@disableBoardsDropdown={{@disableBoardsDropdown}}
				@selectedBoard={{@selectedBoard}}
				@selectedBoardLoop={{@selectedBoardLoop}}
				@onBoardChange={{this.openBoard}}
				@onBoardLoopChange={{this.openBoardLoop}}
			/>

			<div class="actions">

				<SaveIndicatorComponent />

				{{#if this.isAccountTemplate}}
					<SubmitButtonComponent
						@text="Update dashboards"
						@onTrigger={{this.updateFromTemplate}}
						@theme="blue-outlined"
					/>
				{{else if this.showHowToUseDesigner}}
					<SubmitButtonComponent
						@icon="Video"
						@text="How to use Designer"
						@onTrigger={{this.openDesignerVideoPopup}}
						@theme="blue-outlined"
					/>
				{{/if}}

				<DesignerTopbarNotificationsComponent />

				<div class="closeDesigner">
					<a role="button" {{on "click" (fn this.exitDesigner false)}}>
						<CommonUiIconComponent @name="Close" @size="lg" />
						<ToolTipComponent @text="Exit Designer" />
					</a>
				</div>
			</div>

			<div id="mainSettings">

				<div id="boardViewSwitch" class={{if this.isWallView "wall" "mobile"}}>
					<span
						role="button"
						class="view-toggle"
						{{on "click" (fn this.changeBoardView false)}}
					>
						<CommonUiIconComponent @name="DeviceMobile" @size="sm" />
					</span>
					<FormCheckboxSwitchComponent
						@name="isWall"
						@selected={{this.isWallView}}
						@onChange={{this.changeBoardView}}
						@theme="blue"
					/>
					<span
						role="button"
						class="view-toggle"
						{{on "click" (fn this.changeBoardView true)}}
					>
						<CommonUiIconComponent @name="DeviceDesktop" @size="sm" />
					</span>
				</div>

				{{#unless this.isWallView}}
					<div id="previewControls">
						<TopbarZoomComponent />
					</div>
				{{/unless}}

			</div>
		</div>
	</template>
}
